import { currentDivisionId } from 'common/src/stores/useFiltersStore';
import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Card,
  CardBody,
  Button,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@nextui-org/react';
import SeasonFilter from '../../../../../common/src/components/filters/SeasonFilter';
import useFilterStore from '../../../../../common/src/stores/useFiltersStore';
import useUserStore from '../../../../../common/src/stores/useUserStore';
import { getShipmentHeaders } from 'common/src/api/shipments';
import { getSettlementNoDetailsByExporterId } from 'common/src/api/settlements';
import { logUserAction } from '../../../services/actionsService';
import {
  getDaysSinceArrival,
} from '../../../utils/functions';
import * as XLSX from 'xlsx';
import { Icon } from '@iconify/react';
import { demoUniqueShipments } from '../../../utils/data/demo-pre-settlements';
import useDataStore from '../../../../../common/src/stores/useDataStore';
import Header from '../../../components/layouts/Header';
import {
  Alert,
  EmptyState,
  LoadingState,
  PageLayout,
  StatusChip,
} from '../../../components/ui';
interface ShipmentStatus {
  reference_number: string;
  ship_name: string;
  ship_etd: string;
  ship_eta?: string;
  producer_name?: string;
  hasSettlement: boolean;
  settlementId?: number;
  settlementStatus?: string;
}

const SettlementsControlPage = () => {
  const { userProfile } = useUserStore();
  const { selectedSeason } = useFilterStore();
  const { selectedSpecies } = useDataStore();

  const [loading, setLoading] = useState(true);
  const [shipments, setShipments] = useState<ShipmentStatus[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<
    'all' | 'pending' | 'settled'
  >('all');

  const requestVersionRef = useRef(0);
  const fetchData = async () => {
    if (!userProfile?.exporter_id || !selectedSpecies?.id || !selectedSeason) return;
    const requestVersion = ++requestVersionRef.current;
    const isCurrentRequest = () => requestVersion === requestVersionRef.current;
    try {
      setLoading(true);
      const hasDemo = userProfile?.exporter?.hasDemo;
      const shipmentsData = await getShipmentHeaders(
        userProfile?.exporter_id,
        selectedSeason,
        selectedSpecies?.id
      );

      // Get settlements data

      const settlementsData = await getSettlementNoDetailsByExporterId(
        userProfile?.exporter_id,
        'approved',
        selectedSeason,
        selectedSpecies?.id,
        'all',
        currentDivisionId()
      );
      // console.log(settlementsData);

      // Create a Set of reference numbers that have settlements
      const settledReferences = new Set(
        settlementsData?.map((settlement) => settlement.reference_number) || []
      );

      // Map shipments directly with settlement status
      const processedShipments: ShipmentStatus[] = shipmentsData.map(
        (shipment: any) => {
          const reference = shipment.reference_number;
          return {
            reference_number: reference,
            ship_name: shipment.ship_name,
            ship_etd: shipment.ship_etd,
            ship_eta: shipment.ship_eta,
            hasSettlement: settledReferences.has(reference),
          };
        }
      );

      // Remove duplicates based on reference_number
      const uniqueShipments = Array.from(
        new Map(
          processedShipments.map((item) => [item.reference_number, item])
        ).values()
      ) as ShipmentStatus[];

      if (!isCurrentRequest()) return;
      if (!hasDemo) {
        setShipments(uniqueShipments);
      } else {
        setShipments(demoUniqueShipments);
      }
    } catch (error) {
      if (!isCurrentRequest()) return;
      console.error('Error fetching data:', error);
    } finally {
      if (isCurrentRequest()) setLoading(false);
    }
  };

  useEffect(() => {
    logUserAction('view', 'page', 'settlements-control');
  }, []);

  useEffect(() => {
    console.log(selectedSpecies);
    if (selectedSpecies) {
      fetchData();
    }
    return () => {
      requestVersionRef.current += 1;
    };
  }, [
    selectedSeason,
    userProfile?.exporter_id,
    userProfile?.exporter?.hasDemo,
    selectedSpecies,
  ]);

  const getEstimatedSettlementDate = (eta: string) => {
    const etaDate = new Date(eta);
    const settlementDate = new Date(etaDate);
    settlementDate.setDate(settlementDate.getDate() + 30);
    return settlementDate;
  };

  const getDaysOverdue = (eta: string) => {
    const estimatedDate = getEstimatedSettlementDate(eta);
    const today = new Date();
    const diffTime = today.getTime() - estimatedDate.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const filteredShipments = useMemo(() => {
    return shipments
      .filter((shipment) => {
        if (selectedStatus === 'all') return true;
        if (selectedStatus === 'pending') return !shipment.hasSettlement;
        return shipment.hasSettlement;
      })
      .sort((a, b) => {
        const daysA = a.ship_eta ? getDaysSinceArrival(a.ship_eta) : 0;
        const daysB = b.ship_eta ? getDaysSinceArrival(b.ship_eta) : 0;
        return daysB - daysA; // Sort in descending order
      });
  }, [shipments, selectedStatus]);

  const getDaysColor = (days: number) => {
    if (days > 30) return 'text-red-600 dark:text-red-400';
    if (days > 10) return 'text-yellow-600';
    return 'text-green-600 dark:text-green-400';
  };

  const exportToExcel = () => {
    const dataToExport = filteredShipments.map((shipment) => ({
      Referencia: shipment.reference_number,
      Nave: shipment.ship_name,
      ETA: shipment.ship_eta
        ? new Date(shipment.ship_eta).toLocaleDateString()
        : '',
      'Días desde Arribo': shipment.ship_eta
        ? getDaysSinceArrival(shipment.ship_eta)
        : 0,
      'Fecha estimada de liquidación': shipment.ship_eta
        ? getEstimatedSettlementDate(shipment.ship_eta).toLocaleDateString()
        : 'Falta ETA',
      'Días de atraso':
        !shipment.hasSettlement && shipment.ship_eta
          ? getDaysOverdue(shipment.ship_eta)
          : '-',
      Estado: shipment.hasSettlement ? 'Liquidado' : 'Pendiente',
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Liquidaciones');
    XLSX.writeFile(workbook, `control_liquidaciones_${selectedSeason}.xlsx`);
  };

  return (
    <div className='flex w-full h-screen overflow-x-hidden'>
      <PageLayout>
        <Header
          title='Control de Liquidaciones'
          description='Monitorea qué embarques tienen liquidación y cuáles están pendientes.'
          actions={
            <Button
              size='sm'
              variant='flat'
              startContent={<Icon icon='tabler:download' />}
              onClick={exportToExcel}
            >
              Exportar a Excel
            </Button>
          }
        />
        {userProfile?.exporter?.hasDemo && (
          <Alert variant='default' className='mb-4'>
            Estás viendo una versión de prueba con datos ficticios.
            Contrata CherryMarket para cargar tus datos reales.
          </Alert>
        )}

        <Card shadow='sm'>
          <CardBody className='p-4'>
            <div className='flex flex-col md:flex-row gap-4 mb-4'>
              <SeasonFilter size='md' className='md:max-w-[200px]' />
              <Select
                label='Estado'
                selectedKeys={[selectedStatus]}
                onChange={(e) =>
                  setSelectedStatus(
                    e.target.value as 'all' | 'pending' | 'settled'
                  )
                }
                className='md:max-w-[200px]'
              >
                <SelectItem key='all' value='all'>
                  Todos
                </SelectItem>
                <SelectItem key='pending' value='pending'>
                  Pendientes
                </SelectItem>
                <SelectItem key='settled' value='settled'>
                  Liquidados
                </SelectItem>
              </Select>
            </div>

            {loading ? (
              <LoadingState />
            ) : (
              <Table aria-label='Control de Liquidaciones' removeWrapper>
                <TableHeader>
                  <TableColumn>Referencia</TableColumn>
                  <TableColumn>Nave</TableColumn>
                  <TableColumn>ETA</TableColumn>
                  <TableColumn className='text-right'>
                    Días desde Arribo
                  </TableColumn>
                  <TableColumn>Fecha est. liquidación</TableColumn>
                  <TableColumn className='text-right'>Días de atraso</TableColumn>
                  <TableColumn>Estado</TableColumn>
                </TableHeader>
                <TableBody emptyContent={<EmptyState title='Sin embarques' />}>
                  {filteredShipments.map((shipment) => (
                    <TableRow key={shipment.reference_number}>
                      <TableCell className='whitespace-nowrap'>
                        {shipment.reference_number}
                      </TableCell>
                      <TableCell className='whitespace-nowrap'>
                        {shipment.ship_name}
                      </TableCell>
                      <TableCell className='whitespace-nowrap'>
                        {shipment.ship_eta
                          ? new Date(shipment.ship_eta).toLocaleDateString()
                          : 'Falta ETA'}
                      </TableCell>
                      <TableCell
                        className={`whitespace-nowrap text-right font-medium ${getDaysColor(
                          getDaysSinceArrival(shipment.ship_eta)
                        )}`}
                      >
                        {shipment.ship_eta
                          ? getDaysSinceArrival(shipment.ship_eta)
                          : 'Falta ETA'}
                      </TableCell>
                      <TableCell className='whitespace-nowrap'>
                        {shipment.ship_eta
                          ? getEstimatedSettlementDate(
                              shipment.ship_eta
                            ).toLocaleDateString()
                          : 'Falta ETA'}
                      </TableCell>
                      <TableCell className='whitespace-nowrap text-right'>
                        {!shipment.hasSettlement && shipment.ship_eta ? (
                          <span
                            className={`${
                              getDaysOverdue(shipment.ship_eta) > 0
                                ? 'text-red-600 dark:text-red-400'
                                : 'text-foreground'
                            }`}
                          >
                            {getDaysOverdue(shipment.ship_eta)}
                          </span>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {shipment.hasSettlement ? (
                          <StatusChip tone='success'>Liquidado</StatusChip>
                        ) : (
                          <StatusChip tone='warning'>Pendiente</StatusChip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardBody>
        </Card>
      </PageLayout>
    </div>
  );
};

export default SettlementsControlPage;
